/**
 * DemoPage — V2 Demo Panel for the 4.0 (non-cloud) platform.
 *
 * 3 sections:
 *   1. Device Selection Card — cascading dropdowns + action buttons (create alarm, rounds, check-in, simulate)
 *   2. Escalation Simulator Card — careflow simulation config + scrollable results table
 *   3. Emergency Alert Table — real-time alerts from NotificationContext with Clear action
 *
 * NotificationContext integration for real-time emergency alerts (4.0 schema).
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../components/v2/NavigationV2.jsx';
import Toast from '../../components/v2/Toast.jsx';
import useToast from '../../hooks/useToast.js';
import { useNotifications } from '../../contexts/NotificationContext.jsx';
import {
    getSession,
    getCampusId,
    getCampusList,
    updateSessionCampus,
    getCampusConfig,
    getCampusTzOffset,
} from '../../services/session.js';
import {
    sortFunc, campusToGuid, userToGuid, getUTCTimestamp, convertHHMMtoSec,
} from '../../libs.jsx';
import {
    fetchResidentsAndDevices, fetchAreasAndDevices, fetchEventCategories,
    createAlarm, clearAlarm, createRounds, createCheckin, simulateCareflow,
    buildAlarmPayload, buildRoundsPayload,
} from './api/demoApi.js';

import DeviceSelectionCard from './components/DeviceSelectionCard.jsx';
import SimulatorCard from './components/SimulatorCard.jsx';
import DemoEmergencyTable from './components/DemoEmergencyTable.jsx';
import AlertSyncIndicator from '../events/alerts/components/AlertSyncIndicator.jsx';

import './demo-page.css';

const DemoPage = () => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const isCloud = Boolean(campusConfig?.is_cloud);
    const tzOffset = getCampusTzOffset(campusId);

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time emergency alerts, 4.0 schema)
    // ======================================================================
    const {
        activeAlarms, getAlarmById, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE — Reference data
    // ======================================================================
    const [residentData, setResidentData] = useState([]);
    const [areaData, setAreaData] = useState([]);
    const [eventCatData, setEventCatData] = useState([]);

    // ======================================================================
    // STATE — Device Selection (cascading)
    // ======================================================================
    const [server, setServer] = useState('');
    const [dataType, setDataType] = useState('');
    const [entityIndex, setEntityIndex] = useState('');
    const [deviceIndex, setDeviceIndex] = useState('');

    // ======================================================================
    // STATE — Simulator Config
    // ======================================================================
    const [eventCatId, setEventCatId] = useState(1);
    const [duration, setDuration] = useState(30);
    const [timeOfDay, setTimeOfDay] = useState('');
    const [simulateData, setSimulateData] = useState([]);

    // ======================================================================
    // STATE — Loading / Clearing
    // ======================================================================
    const [loadingAlarm, setLoadingAlarm] = useState(false);
    const [loadingRounds, setLoadingRounds] = useState(false);
    const [loadingCheckin, setLoadingCheckin] = useState(false);
    const [loadingSimulate, setLoadingSimulate] = useState(false);
    const [clearingAlarmId, setClearingAlarmId] = useState(null);

    // ======================================================================
    // STATE — Emergency Table Sort
    // ======================================================================
    const [emergSort, setEmergSort] = useState({ col: 'EventDateTime', dir: 'asc' });

    // ======================================================================
    // TOAST
    // ======================================================================
    const { toast, showToast, dismissToast } = useToast(6000);

    // ======================================================================
    // DATA LOADING — residents, areas, event categories
    // ======================================================================
    const loadData = useCallback(async () => {
        if (!campusId) return;
        try {
            const [residents, areas, categories] = await Promise.all([
                fetchResidentsAndDevices(campusId),
                fetchAreasAndDevices(campusId),
                fetchEventCategories(campusId),
            ]);
            setResidentData(Array.isArray(residents) ? residents : []);
            setAreaData(Array.isArray(areas) ? areas : []);
            const cats = Array.isArray(categories) ? categories : [];
            setEventCatData(cats);
            if (cats.length > 0 && eventCatId === 1) {
                setEventCatId(cats[0].event_category_id);
            }
        } catch (err) {
            console.error('[DemoPage] loadData error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Re-poll every 20 minutes (matching V1's 1200s interval)
    useEffect(() => {
        const timer = setInterval(loadData, 1200000);
        return () => clearInterval(timer);
    }, [loadData]);

    // ======================================================================
    // SET DEFAULT SERVER on campusId change
    // ======================================================================
    useEffect(() => {
        const servers = session?.campusData?.[campusId?.toString()]?.Servers || [];
        const first = servers.find((s) => s.mac_addr);
        setServer(first?.mac_addr || '');
        // Reset cascading selections
        setDataType('');
        setEntityIndex('');
        setDeviceIndex('');
        setSimulateData([]);
    }, [campusId, session]);

    // ======================================================================
    // DERIVED VALUES
    // ======================================================================
    const serverOptions = useMemo(() => {
        const servers = session?.campusData?.[campusId?.toString()]?.Servers || [];
        return servers
            .filter((s) => s.mac_addr)
            .map((s) => ({ value: s.mac_addr, label: `${s.description} (${s.mac_addr})` }));
    }, [session, campusId]);

    const entityOptions = useMemo(() => {
        if (!dataType) return [];
        const src = dataType === 'resident' ? residentData : areaData;
        const sortKey = dataType === 'resident' ? 'resident_first_name' : 'area_name';
        const sorted = [...src].sort((a, b) => sortFunc(a, b, sortKey, 'asc'));

        const withDevices = [];
        const withoutDevices = [];
        sorted.forEach((item, i) => {
            const label = dataType === 'resident'
                ? `${item.resident_first_name} ${item.resident_last_name}`
                : item.area_name;
            if (item.devices?.length > 0) {
                withDevices.push({ value: i.toString(), label, disabled: false });
            } else {
                withoutDevices.push({ value: i.toString(), label: `${label} (NO DEVICES)`, disabled: true });
            }
        });
        return [...withDevices, ...withoutDevices];
    }, [dataType, residentData, areaData]);

    const deviceOptions = useMemo(() => {
        if (!dataType || entityIndex === '') return [];
        const src = dataType === 'resident' ? residentData : areaData;
        const entity = src[parseInt(entityIndex, 10)];
        if (!entity?.devices?.length) return [];
        const sorted = [...entity.devices].sort((a, b) => sortFunc(a, b, 'device_name', 'asc'));
        return sorted.map((d, i) => ({
            value: i.toString(),
            label: `${d.device_name} (${d.manufacture_uuid})`,
        }));
    }, [dataType, residentData, areaData, entityIndex]);

    const selectedDevice = useMemo(() => {
        if (!dataType || entityIndex === '' || deviceIndex === '') return null;
        const src = dataType === 'resident' ? residentData : areaData;
        return src[parseInt(entityIndex, 10)]?.devices?.[parseInt(deviceIndex, 10)] || null;
    }, [dataType, residentData, areaData, entityIndex, deviceIndex]);

    const isAioDevice = useMemo(() =>
        selectedDevice?.device_name?.toLowerCase().includes('aio') || false,
    [selectedDevice]);

    const canSubmit = Boolean(selectedDevice);

    const sortedEventCats = useMemo(() =>
        [...(eventCatData || [])].sort((a, b) => sortFunc(a, b, 'event_category_name', 'asc')),
    [eventCatData]);

    const sortedEmergency = useMemo(() => {
        const sorted = [...(activeAlarms || [])];
        sorted.sort((a, b) => sortFunc(a, b, emergSort.col, emergSort.dir));
        return sorted;
    }, [activeAlarms, emergSort]);

    // ======================================================================
    // HANDLERS — Cascading resets
    // ======================================================================
    const handleDataTypeChange = useCallback((val) => {
        setDataType(val);
        setEntityIndex('');
        setDeviceIndex('');
    }, []);

    const handleEntityChange = useCallback((val) => {
        setEntityIndex(val);
        setDeviceIndex('');
    }, []);

    // ======================================================================
    // HANDLERS — Campus
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Non-cloud → cloud: reload so Demo.jsx dispatcher picks CloudDemoPage
        if (cfg?.is_cloud && !isCloud) {
            updateSessionCampus(val);
            window.location.reload();
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
    }, [session, isCloud]);

    // ======================================================================
    // HANDLERS — Actions
    // ======================================================================
    const handleCreateAlarm = useCallback(async () => {
        if (!selectedDevice) return;
        setLoadingAlarm(true);
        const src = dataType === 'resident' ? residentData : areaData;
        const entity = src[parseInt(entityIndex, 10)];
        const payload = buildAlarmPayload({
            dataType,
            resident: dataType === 'resident' ? entity : null,
            area: dataType === 'area' ? entity : null,
            device: selectedDevice,
            serverId: server,
            eventType: 'Emergency',
            session,
            campusId,
            residentData,
        });
        const result = await createAlarm(payload);
        setLoadingAlarm(false);
        if (result[0]) {
            showToast('success', `Alert created for ${selectedDevice.device_name}`);
        } else {
            showToast('error', `Failed to create alarm for ${selectedDevice.device_name}`);
        }
    }, [selectedDevice, dataType, residentData, areaData, entityIndex, server, session, campusId, showToast]);

    const handleRounds = useCallback(async () => {
        if (!selectedDevice || dataType !== 'area') return;
        setLoadingRounds(true);
        const area = areaData[parseInt(entityIndex, 10)];
        const v2Payload = buildAlarmPayload({
            dataType: 'area', resident: null, area, device: selectedDevice,
            serverId: server, eventType: '', session, campusId, residentData,
        });
        const payload = buildRoundsPayload({
            deviceId: selectedDevice.device_id,
            residentId: v2Payload.residents?.[0]?.resident_id,
            areaId: area.area_id,
            campusId,
            tzOffset,
        });
        const result = await createRounds(payload, campusId, session?.user, session?.sessionId);
        setLoadingRounds(false);
        if (result[0]) {
            showToast('success', `Rounds triggered with ${selectedDevice.device_name} in ${area.area_name}`);
        } else {
            showToast('error', `Failed to create rounds with ${selectedDevice.device_name}`);
        }
    }, [selectedDevice, dataType, areaData, entityIndex, server, session, campusId, residentData, tzOffset, showToast]);

    const handleCheckin = useCallback(async () => {
        if (!selectedDevice || dataType !== 'area') return;
        setLoadingCheckin(true);
        const area = areaData[parseInt(entityIndex, 10)];
        const v2Payload = buildAlarmPayload({
            dataType: 'area', resident: null, area, device: selectedDevice,
            serverId: server, eventType: '', session, campusId, residentData,
        });
        const payload = buildRoundsPayload({
            deviceId: selectedDevice.device_id,
            residentId: v2Payload.residents?.[0]?.resident_id,
            areaId: area.area_id,
            campusId,
            tzOffset,
        });
        const result = await createCheckin(payload, campusId, session?.user, session?.sessionId);
        setLoadingCheckin(false);
        if (result[0]) {
            const resName = v2Payload.residents?.[0];
            showToast('success', `${resName?.firstName || ''} ${resName?.lastName || ''} Checked in.`);
        } else {
            showToast('error', 'Failed to check-in');
        }
    }, [selectedDevice, dataType, areaData, entityIndex, server, session, campusId, residentData, tzOffset, showToast]);

    const handleSimulate = useCallback(async () => {
        if (!selectedDevice) return;
        setLoadingSimulate(true);
        setSimulateData([]);
        const timeofday = timeOfDay ? convertHHMMtoSec(timeOfDay) : undefined;
        const data = await simulateCareflow({
            campusId,
            deviceId: selectedDevice.device_id,
            timeofday,
            runtime: duration,
            eventCategoryId: eventCatId
        });
        setSimulateData(data || []);
        setLoadingSimulate(false);
    }, [selectedDevice, campusId, timeOfDay, duration, eventCatId]);

    const handleClearAlarm = useCallback(async (alarmId) => {
        setClearingAlarmId(alarmId);
        const alarm = getAlarmById(alarmId);
        if (!alarm) {
            showToast('error', 'No alarm found to clear');
            setClearingAlarmId(null);
            return;
        }
        const payload = {
            id: alarmId,
            eventDateTime: alarm.EventDateTime,
            mac: alarm.MacAddr,
            ackDateTime: getUTCTimestamp(),
            stage: 'DONE',
            type: alarm.AlertType,
            CampusId: campusToGuid(session, campusId),
        };
        const result = await clearAlarm(payload);
        setClearingAlarmId(null);
        if (result[0]) {
            showToast('success', `${alarm.DeviceUse} ${alarm.SerialNumber} cleared`);
        } else {
            showToast('error', `Failed to clear ${alarm.DeviceUse}`);
        }
    }, [session, campusId, getAlarmById, showToast]);

    const handleEmergSort = useCallback((col) => {
        setEmergSort((prev) => prev.col === col
            ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { col, dir: 'asc' });
    }, []);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="dm-page">
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Header */}
                    <div className="na-header">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Demo Panel</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <div className="ps-campus-row">
                                <label htmlFor="dm-campus" className="ps-campus-label">Campus:</label>
                                {campusList.length > 1 ? (
                                    <select id="dm-campus" className="ps-campus-select" value={campusId} onChange={handleCampusChange}>
                                        {campusList.map((c) => (
                                            <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="dm-content">
                        {/* Two-card row */}
                        <div className="dm-card-row">
                            <DeviceSelectionCard
                                serverOptions={serverOptions}
                                server={server}
                                onServerChange={setServer}
                                dataType={dataType}
                                onDataTypeChange={handleDataTypeChange}
                                entityOptions={entityOptions}
                                entityIndex={entityIndex}
                                onEntityChange={handleEntityChange}
                                entityLabel={dataType === 'resident' ? 'Resident' : dataType === 'area' ? 'Area' : 'Entity'}
                                deviceOptions={deviceOptions}
                                deviceIndex={deviceIndex}
                                onDeviceChange={setDeviceIndex}
                                canSubmit={canSubmit}
                                isAioDevice={isAioDevice}
                                loadingAlarm={loadingAlarm}
                                loadingRounds={loadingRounds}
                                loadingCheckin={loadingCheckin}
                                onCreateAlarm={handleCreateAlarm}
                                onRounds={handleRounds}
                                onCheckin={handleCheckin}
                                onSimulate={handleSimulate}
                                loadingSimulate={loadingSimulate}
                            />
                            <SimulatorCard
                                eventCatData={sortedEventCats}
                                eventCatId={eventCatId}
                                onEventCatChange={setEventCatId}
                                duration={duration}
                                onDurationChange={setDuration}
                                timeOfDay={timeOfDay}
                                onTimeOfDayChange={setTimeOfDay}
                                simulateData={simulateData}
                                loadingSimulate={loadingSimulate}
                            />
                        </div>

                        {/* Emergency Alert Table */}
                        <div>
                            <h3 className="dm-section-title">Active Emergency Alerts</h3>
                            <DemoEmergencyTable
                                data={sortedEmergency}
                                sortColumn={emergSort.col}
                                sortOrder={emergSort.dir}
                                onSort={handleEmergSort}
                                onClear={handleClearAlarm}
                                clearing={clearingAlarmId}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DemoPage;
